import React, { useState, useEffect, useRef, createContext } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { Form as FinalForm } from 'react-final-form';
import { IconPenM, IconEyeL } from '../../icons';
import routeConfiguration from '../../routeConfiguration';
import css from './ProfileSettingsForm.css';

import EditUserInfoModal, { EDIT_SECTIONS } from './EditUserInfoModal';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { userDocumentsFromAssetsData } from '../../util/userDocumentsFromAssets';
import { isProfileComplete } from '../../util/user';
import EditUserMainInfoSection from './sections/EditUserMainInfoSection/EditUserMainInfoSection';
import TrainingsSection from './sections/TrainingsSection';
import AwardsSection from './sections/AwardsSection';
import RecommendationsSection from './sections/RecommendationsSection';
import SkillsSection from './sections/SkillsSection';
import QualificationSection from './sections/QualificationSection';
import DisciplinesSection from './sections/DisciplinesSection';
import GallerySection from './sections/GallerySection';
import BioSection from './sections/BioSection';
import AvailabilitySection from './sections/AvailabilitySection';
import InterestsSection from './sections/InterestsSection';
import VerificationSection from './sections/VerificationSection';
import LanguagesSection from './sections/LanguagesSection';
import LocationAndMobilitySection from './sections/LocationAndMobilitySection';
import ClubsSection from './sections/ClubsSection';
import { NamedLink, ReportProfile, PreviewDocModal, MatchingRateSection } from '../../components';
import NavigationBar from './NavigationBar';
import config from '../../config';

import ListingsSection from './sections/ListingsSection';
import { useListings } from '../../hooks/useListings';
import useBestMatchingListing from '../../hooks/useBestMatchingListing';
import { RIDER_AVAILABILITY_NOT_AVAILABLE } from '../../marketplace-custom-config';
import { useIsMounted } from '../../hooks/useIsMounted';
import { parse } from '../../util/urlHelpers';
import { GALLERY_FORM } from '../../containers/EditUserInfoPage/EditUserInfoPage';
import { setLastSeenListings } from '../../util/localStorage';
import {
    EDIT_USER_DATA_VIA_SECTION,
    USER_ACCOUNT,
    USER_AVATAR,
    USER_IMAGES_VIDEOS,
} from './config';
import StepsToBecomeAvailableAlert from './StepsToBecomeAvailableAlert';
import UserTipsCarousel from './UserTipsCarousel';
import { trimDisplayNameLastWord } from '../../util/text';
import NearbyListingsSection from './sections/NearbyListingsSection';
import ExtraPackagesSection from './sections/ExtraPackagesSection';
import { checkIfUserIsAnonymous } from '../../util/anonym';
import AcceptInquirySection from './sections/AcceptInquirySection';
import { useEditInfoRequestFromUriParam } from './useEditInfoRequestFromUriParam';
import { ChecklistSection } from './sections/ChecklistSection';
import { useSelector } from 'react-redux';

const { userTypeRider, userTypeHorseowner } = config;

export const ProfileSettingsContext = createContext(undefined);

const ProfileSettingsForm = props => {
    const {
        initialValues,
        user,
        publicUser,
        currentUser,
        isSameUser,
        isPublic,
        onDocsListRequest,
        onUpdateProfile,
        updateInProgress,
        onRemoveSingleDocRequest,
        onUploadSingleDocRequest,
        userDocErrorMetadata,
        externalReviewsErrors,
        externalReviewsRequests,
        externalReviewsData,
        isLoggedOut,
        onPromptUserBio,
        promptUserBioInProgress,
        promptUserBioError,
        promptUserBio,
        inquiryDisabled,
        history,
        location,
        intl,
        params,
        isMobile,
        showNavBar,
        computing,
    } = props;

    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [editSectionName, setEditSectionName] = useState(null);
    const [modalParams, setModalParams] = useState(null);
    const [removeParamsOnModalClose, setRemoveParamsOnModalClose] = useState(false);

    const appIsMounted = useIsMounted();
    const uploadAvatarRef = useRef();

    const assetsData = useSelector(s => s.Assets.assetsData);
    const assetsLoadingRequests = useSelector(s => s.Assets.assetsLoadingRequests);
    const allListings = useSelector(s => s.marketplaceData.entities.listing);
    const allOwnListings = useSelector(s => s.marketplaceData.entities.ownListing);

    const {
        attributes: {
            profile: {
                publicData: { userType: currentUserType, mainHorseId },
            },
        },
    } = currentUser;

    const userIdData = user ? user.id : null;
    const userUuid = userIdData ? userIdData.uuid : null;

    const userDocuments = userDocumentsFromAssetsData(userUuid, assetsData);
    const uploadDocumentsInProgress = assetsLoadingRequests && assetsLoadingRequests[userUuid];

    const parsedParams = parse(location.search);
    const { attributes, id } = user;
    const { profile, emailVerified: emailVerifiedFromAttr } = attributes;
    const { publicData, bio, displayName } = profile;
    const {
        phoneVerified,
        city,
        postalCode,
        availability,
        openForPartTimeProposals,
        mobility,
        interest,
        languages,
        riderQualification,
        riderCertification,
        skillsGeneral,
        skillsExperience,
        awards,
        trainings,
        emailVerified: emailVerifiedFromPD,
        availabilityStatus,
        userType,
    } = publicData;

    const isRider = userType === userTypeRider;
    const isOwner = userType === userTypeHorseowner;
    const emailVerified = emailVerifiedFromAttr || emailVerifiedFromPD;
    const { isPrimaryFieldsComplete } = isProfileComplete(user);

    const { userType: pageUserType } = params || {};
    const isRiderPage = pageUserType === userTypeRider;
    const currentUserIsOwner = currentUserType === userTypeHorseowner;
    const currentUserIsRider = currentUserType === userTypeRider;
    const currentUserId = !isLoggedOut && currentUser ? currentUser.id.uuid : null;
    const userId = !isLoggedOut && id ? id.uuid : null;
    const publicUserId = isPublic && id ? id.uuid : null;

    const [userName] = (displayName || '').split(' ');

    const handleEditSection = sectionName => {
        if (EDIT_SECTIONS[sectionName]) {
            setEditSectionName(sectionName);
            setEditModalOpen(true);
        }
    };

    const triggerEditSection = sectionName => {
        /**
         * The event-based communication may be used
         * to trigger independent functionality from
         * different parts of the for, e.g. checklist
         * profile image item has to initiate avatar
         * uploading process developed at UploadUserAvatar.js component
         */
        const editUserDataEvent = new Event(EDIT_USER_DATA_VIA_SECTION);
        editUserDataEvent.details = { sectionName };
        window.dispatchEvent(editUserDataEvent);
    };

    const handleEditSectionViaEvent = e => {
        if (!e || !e.details || !e.details.sectionName) return;
        /**
         * The basic scenario is to trigger a user data edit
         * section in case it exists in EDIT_SECTIONS config;
         * other independent components e.g. UploadUserAvatar.js
         * listens to and handles the event dispatched by their own
         */
        const isAvatarEditEvent = e.details.sectionName === USER_AVATAR;
        if (isAvatarEditEvent) {
            return uploadAvatarRef.current.openAvatarModal();
        }

        const isGalleryEditEvent = e.details.sectionName === USER_IMAGES_VIDEOS;
        if (isGalleryEditEvent) {
            const editUserInfo = createResourceLocatorString(
                'EditUserInfo',
                routeConfiguration(),
                {
                    section: GALLERY_FORM,
                    userType,
                },
                {}
            );
            return history.push(editUserInfo);
        }

        const isAccountEditEvent = e.details.sectionName === USER_ACCOUNT;
        if (isAccountEditEvent) {
            history.push(
                createResourceLocatorString('ContactDetailsPage', routeConfiguration(), {})
            );
        }

        handleEditSection(e.details.sectionName);
    };

    const renderEditPencilIcon = (sectionName, className, children = null, props = {}) =>
        isPublic ? null : (
            <div
                className={css.editPencilIcon}
                onClick={() => handleEditSection(sectionName)}
                {...props}
            >
                <IconPenM className={className} />
                {children}
            </div>
        );

    useEffect(() => {
        window.addEventListener(EDIT_USER_DATA_VIA_SECTION, handleEditSectionViaEvent);

        return () => {
            window.removeEventListener(EDIT_USER_DATA_VIA_SECTION, handleEditSectionViaEvent);
        };
    }, []);

    useEditInfoRequestFromUriParam({
        setRemoveParamsOnModalClose,
        handleEditSection,
        setModalParams,
        parsedParams,
    });

    useEffect(() => {
        onDocsListRequest(user.id.uuid);
    }, [user.id.uuid]);
    /**
     * isRider && currentUserIsOwner - public user is rider & current is owner
     * !isRider && currentUserIsRider - public user is owner & current is rider
     * isLoggedOut - use id from url
     */
    const authorId =
        isRider && currentUserIsOwner
            ? userId
            : !isRider && currentUserIsRider
            ? currentUserId
            : isLoggedOut
            ? publicUserId
            : null;

    const [riderListings] = useListings({
        params: { authorId },
        allowed:
            authorId &&
            !isSameUser &&
            !(isRider && currentUserIsRider) &&
            !(isOwner && currentUserIsOwner),
    });

    const riderListing = Array.isArray(riderListings)
        ? riderListings.find(
              ({
                  attributes: {
                      publicData: { userRepresentationId },
                  },
              }) => !!userRepresentationId
          )
        : null;

    const [scoreReqInProgress, score] = useBestMatchingListing({
        riderListing,
        /**
         * currentUser has to be an owner
         */
        ownerId: isRiderPage && currentUserIsOwner && !isSameUser ? currentUserId : null,
        mainHorseId,
    });

    const scoreData = isLoggedOut ? null : score;
    const recommendations = externalReviewsData && externalReviewsData[user.id.uuid];
    const riderListingId = riderListing && riderListing.id && riderListing.id.uuid;

    const viewerListing =
        allListings?.[mainHorseId] ??
        allOwnListings?.[mainHorseId] ??
        (allOwnListings ? Object.values(allOwnListings)[0] : null);

    useEffect(() => {
        riderListingId && setLastSeenListings(riderListingId);
    }, [riderListingId]);

    return (
        <ProfileSettingsContext.Provider value={{ handleEditSection, setModalParams }}>
            <FinalForm
                {...props}
                initialValues={{
                    ...initialValues,
                }}
                render={fieldRenderProps => {
                    const {
                        className,
                        user,
                        onImageUpload,
                        profileImage,
                        rootClassName,
                        uploadImageError,
                        uploadInProgress,
                        form,
                    } = fieldRenderProps;

                    const {
                        attributes: {
                            profile: {
                                publicData: { userType },
                            },
                        },
                    } = user;

                    const showAlert =
                        (!isPrimaryFieldsComplete ||
                            availabilityStatus === RIDER_AVAILABILITY_NOT_AVAILABLE) &&
                        !isPublic &&
                        isRider;

                    const logoutCommonProps = {
                        pageParams: params,
                        isLoggedOut,
                        displayName: trimDisplayNameLastWord(userName || ''),
                        switchUserRoleAction:
                            appIsMounted &&
                            !isLoggedOut &&
                            ((!isSameUser && isRider && currentUserType === userTypeRider) ||
                                (!isSameUser &&
                                    !isRider &&
                                    currentUserType === userTypeHorseowner)),
                    };

                    const commonProps = {
                        isLoggedOut,
                        isPublic,
                        scoreData,
                        scoreReqInProgress,
                        renderEditPencilIcon,
                        handleEditSection,
                        setModalParams,
                    };

                    const checklistSection = !isPublic && isRider && (
                        <ChecklistSection
                            currentUser={user}
                            userDocuments={userDocuments}
                            triggerEditSection={triggerEditSection}
                            onUpdateProfile={onUpdateProfile}
                            uploadDocumentsInProgress={uploadDocumentsInProgress}
                        />
                    );

                    const extraPackagesSection = computing
                        ? null
                        : !isPublic &&
                          isRider && (
                              <ExtraPackagesSection
                                  currentUser={currentUser}
                                  handleEditSection={handleEditSection}
                              />
                          );

                    const matchingRateSection = isPublic && (
                        <MatchingRateSection
                            currentUser={currentUser}
                            user={user}
                            isLoggedOut={isLoggedOut}
                            isRiderPage={isRider}
                            isSameUser={isSameUser}
                            currentPageListing={isRider ? riderListing : null}
                            viewerListing={viewerListing}
                            scoreReqInProgress={scoreReqInProgress}
                            scoreData={scoreData}
                            dateFormatConfig={{ year: 'numeric' }}
                        />
                    );

                    const locationAndMobilitySection = (
                        <LocationAndMobilitySection
                            mobility={mobility}
                            city={city}
                            postalCode={postalCode}
                            userType={userType}
                            {...commonProps}
                        />
                    );

                    const availabilitySection = isRider && (
                        <AvailabilitySection
                            availability={availability}
                            openForPartTimeProposals={openForPartTimeProposals}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const interestsSection = isRider && (
                        <InterestsSection
                            interest={interest}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const verificationSection = (
                        <VerificationSection
                            phoneVerified={phoneVerified}
                            emailVerified={emailVerified}
                            {...commonProps}
                        />
                    );

                    const languagesSection = (
                        <LanguagesSection
                            languages={languages}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const bioSection = (
                        <BioSection
                            bio={bio}
                            isRider={isRider}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const listingSection = isPublic && isOwner && (
                        <ListingsSection user={user} currentUser={currentUser} />
                    );

                    const gallerySection = isRider && (
                        <GallerySection
                            rootClassName={rootClassName}
                            uploadDocumentsInProgress={uploadDocumentsInProgress}
                            userDocuments={userDocuments}
                            className={className}
                            userType={userType}
                            isAnonym={isPublic && checkIfUserIsAnonymous(user)}
                            acceptInquirySection={
                                isPublic && (
                                    <AcceptInquirySection
                                        user={user}
                                        userName={logoutCommonProps.displayName}
                                        isSameUser={isSameUser}
                                        disabled={inquiryDisabled}
                                        riderPage
                                        isLoggedOut={isLoggedOut}
                                        transactionDataInProgress={
                                            props.activeTransactionsDataInProgress
                                        }
                                        riderListingId={riderListing?.id?.uuid}
                                        rootClassName={css.galleryAcceptInquirySection}
                                    />
                                )
                            }
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const disciplinesSection = isRider && (
                        <DisciplinesSection
                            publicData={publicData}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const qualificationSection = isRider && (
                        <QualificationSection
                            qualifications={riderQualification}
                            certifications={riderCertification}
                            uploadDocumentsInProgress={uploadDocumentsInProgress}
                            userDocuments={userDocuments}
                            setPreviewModalOpen={setPreviewModalOpen}
                            setPreviewDoc={setPreviewDoc}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const skillsSection = isRider && (
                        <SkillsSection
                            skillsGeneral={skillsGeneral}
                            skillsExperience={skillsExperience}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const recommendationsSection = isRider && (
                        <RecommendationsSection
                            isPublic={isPublic}
                            currentUser={user}
                            externalReviewsErrors={externalReviewsErrors}
                            externalReviewsRequests={externalReviewsRequests}
                            recommendations={recommendations}
                            isSameUser={isSameUser}
                            intl={intl}
                            {...logoutCommonProps}
                        />
                    );

                    const awardsSection = isRider && (
                        <AwardsSection
                            awards={awards}
                            userDocuments={userDocuments}
                            uploadDocumentsInProgress={uploadDocumentsInProgress}
                            setPreviewModalOpen={setPreviewModalOpen}
                            setPreviewDoc={setPreviewDoc}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const trainingsSection = isRider && (
                        <TrainingsSection
                            trainings={trainings}
                            userDocuments={userDocuments}
                            uploadDocumentsInProgress={uploadDocumentsInProgress}
                            setPreviewModalOpen={setPreviewModalOpen}
                            setPreviewDoc={setPreviewDoc}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    const reportProfile = (
                        <ReportProfile
                            user={publicUser}
                            type="profile"
                            initialValues={{ type: 'profile' }}
                        />
                    );

                    const nearbyListingsSection = (
                        <NearbyListingsSection
                            user={user}
                            currentUser={currentUser}
                            scoreData={scoreData}
                            isLoggedOut={isLoggedOut}
                            riderListing={riderListing}
                        />
                    );
                    /**
                     * show the section only for riders' profiles
                     */
                    const showNearbyListingsSection =
                        isPublic && !isSameUser && isRider && isPrimaryFieldsComplete;

                    const clubsSection = (
                        <ClubsSection
                            publicData={publicData}
                            {...logoutCommonProps}
                            {...commonProps}
                        />
                    );

                    return (
                        <>
                            {user.id && !isPublic ? (
                                <NamedLink
                                    className={css.profileLink}
                                    name="ProfilePagePreview"
                                    params={{ id: user.id.uuid, userType }}
                                >
                                    <IconEyeL />
                                    <FormattedMessage id="ProfileSettingsPage.viewProfileLink" />
                                </NamedLink>
                            ) : null}
                            {!isPublic && <UserTipsCarousel />}
                            {showAlert && (
                                <StepsToBecomeAvailableAlert
                                    user={user}
                                    handleEditSection={handleEditSection}
                                    setModalParams={setModalParams}
                                />
                            )}
                            {showNavBar && isRider && (
                                <NavigationBar
                                    user={user}
                                    recommendations={externalReviewsData || []}
                                    userDocuments={userDocuments}
                                    currentUser={currentUser}
                                />
                            )}
                            {isEditModalOpen && (
                                <EditUserInfoModal
                                    uploadDocumentsInProgress={uploadDocumentsInProgress}
                                    onRemoveSingleDocRequest={onRemoveSingleDocRequest}
                                    onUploadSingleDocRequest={onUploadSingleDocRequest}
                                    userDocErrorMetadata={userDocErrorMetadata}
                                    userDocuments={userDocuments}
                                    isOpen={isEditModalOpen}
                                    editSectionName={editSectionName}
                                    user={user}
                                    currentUser={currentUser}
                                    onDocsListRequest={() => onDocsListRequest(user.id.uuid)}
                                    updateInProgress={updateInProgress}
                                    modalParams={modalParams}
                                    onPromptUserBio={onPromptUserBio}
                                    promptUserBioInProgress={promptUserBioInProgress}
                                    promptUserBioError={promptUserBioError}
                                    promptUserBio={promptUserBio}
                                    setModalParams={setModalParams}
                                    onClose={() => {
                                        setEditModalOpen(false);
                                        setEditSectionName(null);
                                        setModalParams(null);

                                        if (removeParamsOnModalClose) {
                                            setRemoveParamsOnModalClose(false);
                                            history.replace(
                                                createResourceLocatorString(
                                                    'ProfileSettingsPage',
                                                    routeConfiguration(),
                                                    { userType: userTypeRider },
                                                    {}
                                                )
                                            );
                                        }
                                    }}
                                />
                            )}
                            <EditUserMainInfoSection
                                uploadAvatarRef={uploadAvatarRef}
                                form={form}
                                isSameUser={isSameUser}
                                user={user}
                                currentUser={currentUser}
                                uploadInProgress={uploadInProgress}
                                isPublic={isPublic}
                                riderPage={isRider}
                                onImageUpload={onImageUpload}
                                renderEditPencilIcon={renderEditPencilIcon}
                                image={profileImage}
                                uploadImageError={uploadImageError}
                                onUpdateProfile={onUpdateProfile}
                                inquiryDisabled={inquiryDisabled}
                                intl={intl}
                                isMobile={isMobile}
                                recommendations={recommendations}
                                externalReviewsRequests={externalReviewsRequests}
                                handleEditSection={handleEditSection}
                                riderListing={riderListing}
                                scoreData={scoreData}
                                isLoggedOut={isLoggedOut}
                                transactionDataInProgress={props.activeTransactionsDataInProgress}
                            />

                            {!isMobile ? (
                                <section className={css.mainInfoSectionDesktop}>
                                    <aside className={css.asideMainInfo}>
                                        {checklistSection}
                                        {extraPackagesSection}
                                        {matchingRateSection}
                                        {locationAndMobilitySection}
                                        {availabilitySection}
                                        {interestsSection}
                                        {clubsSection}
                                        {verificationSection}
                                        {languagesSection}
                                        {isPublic && !isSameUser && reportProfile}
                                    </aside>
                                    <main className={css.mainInfo}>
                                        {bioSection}
                                        {listingSection}
                                        {gallerySection}
                                        {disciplinesSection}
                                        {qualificationSection}
                                        {skillsSection}
                                        {recommendationsSection}
                                        {awardsSection}
                                        {trainingsSection}
                                    </main>
                                </section>
                            ) : (
                                <section className={css.mainInfoSectionMob}>
                                    {checklistSection}
                                    {extraPackagesSection}
                                    {matchingRateSection}
                                    {locationAndMobilitySection}
                                    {availabilitySection}
                                    {interestsSection}
                                    {bioSection}
                                    {listingSection}
                                    {gallerySection}
                                    {disciplinesSection}
                                    {qualificationSection}
                                    {skillsSection}
                                    {recommendationsSection}
                                    {awardsSection}
                                    {trainingsSection}
                                    {clubsSection}
                                    {languagesSection}
                                    {verificationSection}
                                    {isPublic && !isSameUser && reportProfile}
                                    {showNearbyListingsSection && nearbyListingsSection}
                                </section>
                            )}
                            {isPreviewModalOpen && previewDoc && (
                                <PreviewDocModal
                                    isOpen
                                    previewDoc={previewDoc}
                                    onClose={() => {
                                        setPreviewModalOpen(false);
                                        setPreviewDoc(null);
                                    }}
                                />
                            )}
                        </>
                    );
                }}
            />
        </ProfileSettingsContext.Provider>
    );
};

export default compose(injectIntl, withRouter)(ProfileSettingsForm);
